import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import type {
    CrewKind,
    ServiceCommunityWithSection,
    ServiceItemState,
    ServiceItemStatus,
    ServiceItemWithContext,
    ServiceSection,
} from './serviceTypes';

// Follows the same shape as queryKeys in queries.ts. Kept separate so the
// service board can be invalidated without blowing the schedule board's cache,
// and so a future Phase 4 handoff can read one file to understand service.
export const serviceKeys = {
    sections: ['serviceSections'] as const,
    communities: ['serviceCommunities'] as const,
    crews: (kind: CrewKind | 'all') => ['serviceCrews', kind] as const,
    items: {
        all: ['serviceItems'] as const,
        counts: ['serviceItems', 'counts'] as const,
        list: (filters?: ServiceItemFilters) => ['serviceItems', 'list', filters ?? {}] as const,
        detail: (id: string) => ['serviceItems', 'detail', id] as const,
        route: (crewId: string | undefined, date: string | undefined) =>
            ['serviceItems', 'route', crewId ?? 'none', date ?? 'none'] as const,
    },
} as const;

export interface ServiceItemFilters {
    status?: ServiceItemStatus[];
    state?: ServiceItemState[];
    crewId?: string;
    communityId?: string;
    sectionId?: string;
    billable?: boolean;
    requiresConcrete?: boolean;
}

export function useInvalidateServiceItems() {
    const qc = useQueryClient();
    return (id?: string) => {
        qc.invalidateQueries({ queryKey: serviceKeys.items.all });
        if (id) qc.invalidateQueries({ queryKey: serviceKeys.items.detail(id) });
        qc.invalidateQueries({ queryKey: ['activityLog'] });
    };
}

// -----------------------------------------------------------------------------
// Reference data
// -----------------------------------------------------------------------------

export function useServiceSections() {
    return useQuery({
        queryKey: serviceKeys.sections,
        queryFn: async (): Promise<ServiceSection[]> => {
            const { data, error } = await supabase
                .from('service_sections')
                .select('*')
                .order('sort_order');
            if (error) throw error;
            return data ?? [];
        },
        staleTime: 1000 * 60 * 60,
    });
}

export function useServiceCommunities() {
    return useQuery({
        queryKey: serviceKeys.communities,
        queryFn: async (): Promise<ServiceCommunityWithSection[]> => {
            const { data, error } = await supabase
                .from('service_communities')
                .select('*, section:service_sections ( id, name, sort_order, color_key )')
                .eq('active', true)
                .order('builder_name')
                .order('name');
            if (error) throw error;
            return (data ?? []) as ServiceCommunityWithSection[];
        },
        staleTime: 1000 * 60 * 30,
    });
}

/**
 * Crews for a given side of the business.
 *
 * crews.kind exists because JL, MAC and R&R do both slab and service work while
 * Alex and Leon are pour only and Jonathan and Austin are service only. Passing
 * 'service' here is what keeps the two techs out of the schedule board's crew
 * picker, and passing 'pour' keeps them out of Andrea's.
 */
export function useServiceCrews(kind: CrewKind = 'service') {
    return useQuery({
        queryKey: serviceKeys.crews(kind),
        queryFn: async (): Promise<{ id: string; name: string; kind: CrewKind }[]> => {
            const { data, error } = await supabase
                .from('crews')
                .select('id, name, kind')
                .eq('active', true)
                .in('kind', [kind, 'both'])
                .order('name');
            if (error) throw error;
            return data ?? [];
        },
        staleTime: 1000 * 60 * 10,
    });
}

// -----------------------------------------------------------------------------
// Service items
// -----------------------------------------------------------------------------

// Two crew embeds at the same level and they are not the same thing.
// crew_id is who is doing the work. backcharge_crew_id is who is being charged
// for it when the work is fixing another crew's mistake. Sara tracks both in
// separate columns on the sheet and they are frequently different.
const SERVICE_ITEM_SELECT = `
    *,
    community:service_communities (
        id, builder_name, name, display_name,
        section:service_sections ( id, name, sort_order, color_key )
    ),
    crew:crews!service_items_crew_id_fkey ( id, name ),
    backcharge_crew:crews!service_items_backcharge_crew_id_fkey ( id, name )
`;

/**
 * Counts for the Active and Completed tabs.
 *
 * A count query rather than fetching both sets and splitting them in the
 * browser, because the completed side only ever grows and the board should not
 * pull years of finished work down the wire to put a number on a tab. head:true
 * means no rows come back at all, only the count.
 */
export function useServiceStatusCounts() {
    return useQuery({
        queryKey: serviceKeys.items.counts,
        queryFn: async (): Promise<{ active: number; completed: number }> => {
            const [activeRes, completedRes] = await Promise.all([
                supabase
                    .from('service_items')
                    .select('id', { count: 'exact', head: true })
                    .eq('status', 'open'),
                supabase
                    .from('service_items')
                    .select('id', { count: 'exact', head: true })
                    .neq('status', 'open'),
            ]);
            if (activeRes.error) throw activeRes.error;
            if (completedRes.error) throw completedRes.error;
            return {
                active: activeRes.count ?? 0,
                completed: completedRes.count ?? 0,
            };
        },
        staleTime: 1000 * 30,
    });
}

export function useServiceItems(filters?: ServiceItemFilters) {
    return useQuery({
        queryKey: serviceKeys.items.list(filters),
        queryFn: async (): Promise<ServiceItemWithContext[]> => {
            let q = supabase.from('service_items').select(SERVICE_ITEM_SELECT);

            if (filters?.status?.length) q = q.in('status', filters.status);
            if (filters?.state?.length) q = q.in('schedule_state', filters.state);
            if (filters?.crewId) q = q.eq('crew_id', filters.crewId);
            if (filters?.communityId) q = q.eq('community_id', filters.communityId);
            if (filters?.billable !== undefined) q = q.eq('billable', filters.billable);
            if (filters?.requiresConcrete !== undefined) {
                q = q.eq('requires_concrete', filters.requiresConcrete);
            }

            // Oldest request first. Sara works the list from the top and an item
            // that has been sitting since April should not be below one that
            // came in this morning.
            //
            // id is the final tiebreaker and it is not optional. The seed ran in
            // a single transaction, so now() returned the same value for all 79
            // rows and created_at is identical across the table. Without a unique
            // last key, Postgres returns tied rows in arbitrary order and the
            // board reshuffles on every refresh, which quietly destroys trust in
            // a view people read positionally.
            const { data, error } = await q
                .order('email_date', { ascending: true, nullsFirst: false })
                .order('created_at', { ascending: true })
                .order('id', { ascending: true });
            if (error) throw error;

            const rows = (data ?? []) as ServiceItemWithContext[];

            // Section filtering happens here rather than in the query because it
            // reaches through service_communities and PostgREST cannot filter on
            // a nested embed without dropping rows whose community is null. Those
            // rows are exactly the ones the office needs to see and fix.
            if (filters?.sectionId) {
                return rows.filter((r) => r.community?.section?.id === filters.sectionId);
            }
            return rows;
        },
        staleTime: 1000 * 30,
    });
}

export function useServiceItem(id: string | undefined) {
    return useQuery({
        queryKey: id ? serviceKeys.items.detail(id) : ['serviceItems', 'detail', 'none'],
        queryFn: async (): Promise<ServiceItemWithContext | null> => {
            if (!id) return null;
            const { data, error } = await supabase
                .from('service_items')
                .select(SERVICE_ITEM_SELECT)
                .eq('id', id)
                .maybeSingle();
            if (error) throw error;
            return (data as ServiceItemWithContext) ?? null;
        },
        enabled: !!id,
    });
}

/**
 * The daily route: one crew, one date, grouped by community section.
 *
 * Sara's process builds this by hand every afternoon, grouping items in nearby
 * communities onto the same day so techs are not driving between stops. The
 * sections list is what defines "nearby". Items whose community has no section
 * still come back, under a null key, because leaving them out would silently
 * drop work from a tech's day.
 */
export function useServiceRoute(crewId: string | undefined, isoDate: string | undefined) {
    return useQuery({
        queryKey: serviceKeys.items.route(crewId, isoDate),
        queryFn: async (): Promise<ServiceItemWithContext[]> => {
            if (!crewId || !isoDate) return [];
            const { data, error } = await supabase
                .from('service_items')
                .select(SERVICE_ITEM_SELECT)
                .eq('crew_id', crewId)
                .eq('scheduled_date', isoDate)
                .eq('status', 'open')
                // lot here, community in groupRouteBySection. id last so two
                // items on the same lot never swap places between refreshes.
                .order('lot', { ascending: true, nullsFirst: false })
                .order('id', { ascending: true });
            if (error) throw error;
            return (data ?? []) as ServiceItemWithContext[];
        },
        enabled: !!crewId && !!isoDate,
        staleTime: 1000 * 30,
    });
}

/**
 * Open items scheduled for a date with nobody assigned.
 *
 * Without this they are invisible. The route view filters by crew, so an item
 * scheduled for Thursday with a null crew appears on no tech's day and nobody
 * finds out until the builder calls. Surfacing them next to the route is the
 * whole point: a date with work on it and no crew is a mistake, not a state.
 */
export function useUnassignedForDate(isoDate: string | undefined) {
    return useQuery({
        queryKey: ['serviceItems', 'unassigned', isoDate ?? 'none'],
        queryFn: async (): Promise<ServiceItemWithContext[]> => {
            if (!isoDate) return [];
            const { data, error } = await supabase
                .from('service_items')
                .select(SERVICE_ITEM_SELECT)
                .is('crew_id', null)
                .eq('scheduled_date', isoDate)
                .eq('status', 'open')
                .order('lot', { ascending: true, nullsFirst: false })
                .order('id', { ascending: true });
            if (error) throw error;
            return (data ?? []) as ServiceItemWithContext[];
        },
        enabled: !!isoDate,
        staleTime: 1000 * 30,
    });
}

/**
 * Community first, then lot. Used inside each section group.
 *
 * The route used to come back in id order, which on a uuid is effectively
 * random. The 16 Sep M&M list read lot 57, 47, 57, then a stop in Clover SC,
 * then 59, then 47 again. That is a tech driving to the same address twice.
 * All the work at one address has to sit together.
 *
 * lot is text and holds "1,2,3" and "Cons. Pad" alongside plain numbers, so
 * numeric-aware comparison keeps 47 before 57 before 100 rather than the plain
 * text order that would put 100 first.
 */
const routeCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

function compareRouteStops(a: ServiceItemWithContext, b: ServiceItemWithContext): number {
    const ca = a.community?.display_name ?? '';
    const cb = b.community?.display_name ?? '';
    if (ca !== cb) return routeCollator.compare(ca, cb);
    return routeCollator.compare(a.lot ?? '', b.lot ?? '');
}

/** Groups route items by section, in the section order Sara's sheet defines. */
export function groupRouteBySection(items: ServiceItemWithContext[]) {
    const groups = new Map<string, { label: string; sortOrder: number; items: ServiceItemWithContext[] }>();
    for (const item of items) {
        const section = item.community?.section;
        const key = section?.id ?? 'unsectioned';
        if (!groups.has(key)) {
            groups.set(key, {
                label: section?.name ?? 'No section',
                // Unsectioned sinks to the bottom rather than the top, so an
                // unclassified item never leads a tech's day.
                sortOrder: section?.sort_order ?? 999,
                items: [],
            });
        }
        groups.get(key)!.items.push(item);
    }
    // Sorted inside each section rather than in the query, because PostgREST
    // cannot order a parent row by a column on an embedded table and community
    // name lives on service_communities.
    for (const g of groups.values()) g.items.sort(compareRouteStops);
    return [...groups.values()].sort((a, b) => a.sortOrder - b.sortOrder);
}
